import Handsontable from 'handsontable/base';
import { sum } from 'lodash';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { TableContentData, TableElementChanges } from '../../../../common/table/TableTypes';
import useRefCallback from '../../../utils/react/useRefCallback';
import { ActiveTableSession, FlushReason } from '../store/hotTableStoreTypes';
import { updateTableElement } from '../tableActions';
import {
    convertHotInstanceToRaw,
    doesHotSnapshotMatchTableState,
} from '../utils/hotInstanceUtils/persistence/hotTablePersistenceUtils';

export const ACTIVE_TABLE_FLUSH_DEBOUNCE_MS = 1000;

type UseHotToReduxSessionControlsProps = {
    elementId: string;
    tableInstance: Handsontable | null;
    gridSize: number;
    elementWidth: number | undefined;
    tableContentData: TableContentData | undefined;
    tableContentColWidthsGU: number[] | undefined;
};

/**
 * Owns the active-table session controls and the refs they depend on, while keeping a stable session object.
 */
export const useHotToReduxSessionControls = ({
    elementId,
    tableInstance,
    gridSize,
    elementWidth,
    tableContentData,
    tableContentColWidthsGU,
}: UseHotToReduxSessionControlsProps): ActiveTableSession => {
    const elementIdRef = useRef(elementId);
    const tableInstanceRef = useRef(tableInstance);
    const gridSizeRef = useRef(gridSize);
    const elementWidthRef = useRef(elementWidth);
    const tableContentDataRef = useRef(tableContentData);
    const tableContentColWidthsGURef = useRef(tableContentColWidthsGU);
    const flushTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isDirtyRef = useRef(false);

    const dispatch = useDispatch();
    const dispatchUpdateTableElement = useCallback(
        (arg) => dispatch(updateTableElement(arg)),
        [dispatch],
    );

    // Keep the latest inputs available to stable session helpers without recreating those helpers, via refs.
    useEffect(() => {
        elementIdRef.current = elementId;
        tableInstanceRef.current = tableInstance;
        gridSizeRef.current = gridSize;
        elementWidthRef.current = elementWidth;
        tableContentDataRef.current = tableContentData;
        tableContentColWidthsGURef.current = tableContentColWidthsGU;
    }, [elementId, elementWidth, gridSize, tableContentColWidthsGU, tableContentData, tableInstance]);

    /**
     * The debounce timer belongs to the active editing session, so cancel it explicitly on rehydrate/unmount paths.
     */
    const clearScheduledFlush = useRefCallback(() => {
        if (!flushTimeoutRef.current) return;

        clearTimeout(flushTimeoutRef.current);
        flushTimeoutRef.current = null;
    }, []);

    /**
     * Mutation observers mark the session as dirty immediately.
     */
    const markDirtyInternal = useRefCallback(() => {
        isDirtyRef.current = true;
    }, []);

    /**
     * Flush serializes the live HOT snapshot back into persisted element state, but only if Redux is behind.
     */
    const flushNow = useRefCallback(() => {
        clearScheduledFlush();

        if (!isDirtyRef.current) return;
        if (!tableInstanceRef.current) return;

        const tableSnapshot = convertHotInstanceToRaw(tableInstanceRef.current, gridSizeRef.current);

        const isTableSnapshotEqual = doesHotSnapshotMatchTableState({
            tableSnapshot,
            tableContentData: tableContentDataRef.current,
            tableContentColWidthsGU: tableContentColWidthsGURef.current,
        });

        if (isTableSnapshotEqual) {
            isDirtyRef.current = false;
            return;
        }

        const width = sum(tableSnapshot.colWidthsGU);
        const changes: TableElementChanges = { tableContent: tableSnapshot };

        if (width !== elementWidthRef.current) {
            changes.width = width;
        }

        dispatchUpdateTableElement({
            id: elementIdRef.current,
            changes,
        });

        isDirtyRef.current = false;
    }, [clearScheduledFlush, dispatchUpdateTableElement]);

    /**
     * Debounced flush logic.
     */
    const scheduleFlush = useRefCallback(
        (_reason?: FlushReason) => {
            if (!isDirtyRef.current) return;

            clearScheduledFlush();

            flushTimeoutRef.current = setTimeout(() => {
                flushNow();
            }, ACTIVE_TABLE_FLUSH_DEBOUNCE_MS);
        },
        [clearScheduledFlush, flushNow],
    );

    /**
     * Marks the session dirty and schedules a debounced flush.
     */
    const markDirty = useRefCallback(
        (reason?: FlushReason) => {
            markDirtyInternal();
            scheduleFlush(reason);
        },
        [markDirtyInternal, scheduleFlush],
    );

    /**
     * Marks the session dirty and flushes immediately.
     */
    const markDirtyAndFlush = useRefCallback(
        (_reason?: FlushReason) => {
            markDirtyInternal();
            flushNow();
        },
        [flushNow, markDirtyInternal],
    );

    useEffect(() => {
        isDirtyRef.current = false;

        return () => {
            clearScheduledFlush();
            isDirtyRef.current = false;
        };
    }, [clearScheduledFlush, tableInstance]);

    // Expose a stable session object through the active-table store so toolbar/popup consumers can rely on it.
    const sessionRef = useRef<ActiveTableSession>({
        markDirty: (reason?: FlushReason) => markDirty(reason),
        markDirtyAndFlush: (reason?: FlushReason) => markDirtyAndFlush(reason),
        isDirty: () => isDirtyRef.current,
    });

    return sessionRef.current;
};
